import React, { useMemo, useState } from 'react';
import { Alert, Form, Button, Badge, Table, Card, FilterGrid } from '@/components/ui';
import reportService, { OperationsLabourPreviewData } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import YellowHeaderInfo from '../components/YellowHeaderInfo';
import Toast from '../../../components/Toast';
import { ForemanArea, ServiceArea, areasService } from '../../../services/areasService';
import { format } from 'date-fns';

interface OperationsLabourReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const OVERTIME_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'ot', label: 'OT' },
  { value: 'not_ot', label: 'Not OT' }
] as const;
const MATRIX_FETCH_LIMIT = 10000;

type MatrixCell = {
  hasData: boolean;
  entries: Array<{
    formId: string;
    workedHours: number;
    hasOvertime: boolean;
    statusType: 'approved' | 'declined' | 'unapproved';
    statusLabel: string;
    submittedAt: string;
  }>;
  submissionCount: number;
};

const normalizeStatus = (status: string): string => status.trim().toLowerCase();

const parseDateInputAsLocal = (dateInput: string): Date | null => {
  if (!dateInput) return null;
  const [y, m, d] = dateInput.split('-').map((part) => parseInt(part, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const parsed = new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateKey = (dateInput: string | null | undefined): string => {
  if (!dateInput) return '-';
  // Preserve API-provided YYYY-MM-DD/ISO day component exactly to avoid timezone shifts.
  if (typeof dateInput === 'string' && dateInput.length >= 10) {
    return dateInput.slice(0, 10);
  }
  return '-';
};

const buildDateRange = (startDate: string, endDate: string): string[] => {
  if (!startDate || !endDate) return [];
  const start = parseDateInputAsLocal(startDate);
  const end = parseDateInputAsLocal(endDate);
  if (!start || !end || start > end) return [];

  const dates: string[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  while (cursor <= end) {
    dates.push(format(cursor, 'yyyy-MM-dd'));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const OperationsLabourReportView: React.FC<OperationsLabourReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    foremanArea: '',
    startDate: '',
    endDate: '',
    overtimeFilter: 'all'
  });
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);

  const [previewData, setPreviewData] = useState<OperationsLabourPreviewData[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: MATRIX_FETCH_LIMIT,
    total: 0,
    totalPages: 0,
    onPageChange: (_page: number, _pageSize?: number) => {}
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  const handleGeneratePreview = async () => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getOperationsLabourPreviewData({
        serviceArea: filters.serviceArea || undefined,
        foremanArea: filters.foremanArea || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        overtimeFilter: filters.overtimeFilter !== 'all' ? filters.overtimeFilter : undefined,
        page: 1,
        limit: MATRIX_FETCH_LIMIT,
        sortField: 'formDate',
        sortDirection: 'asc'
      });
      setPreviewData(response.data);
      setPagination({
        page: 1,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: 1,
        onPageChange: () => {}
      });
    } catch {
      setErrorMessage('Failed to generate operations labour report preview.');
      setPreviewData([]);
      setPagination({ page: 1, limit: MATRIX_FETCH_LIMIT, total: 0, totalPages: 0, onPageChange: () => {} });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExport = async () => {
    setLoadingExport(true);
    setErrorMessage(null);
    try {
      await reportService.generateOperationsLabourReport({
        serviceArea: filters.serviceArea || undefined,
        foremanArea: filters.foremanArea || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        overtimeFilter: filters.overtimeFilter !== 'all' ? filters.overtimeFilter : undefined
      });
      setToastMessage('Submitted time report exported successfully.');
      setShowToast(true);
    } catch {
      setErrorMessage('Failed to export operations labour report.');
    } finally {
      setLoadingExport(false);
    }
  };

  const { dateColumns, employeeRows, dateTotals } = useMemo(() => {
    const dateSet = new Set<string>();
    const matrix = new Map<string, Map<string, OperationsLabourPreviewData[]>>();

    for (const row of previewData) {
      const employeeName = (row.employeeName || 'Unknown Employee').trim() || 'Unknown Employee';
      const dateKey = row.formDate ? toDateKey(row.formDate) : '';
      if (!dateKey || dateKey === '-') continue;

      dateSet.add(dateKey);

      if (!matrix.has(employeeName)) {
        matrix.set(employeeName, new Map<string, OperationsLabourPreviewData[]>());
      }
      const employeeDateMap = matrix.get(employeeName)!;
      const bucket = employeeDateMap.get(dateKey) || [];
      bucket.push(row);
      employeeDateMap.set(dateKey, bucket);
    }

    const rangeDates = buildDateRange(filters.startDate, filters.endDate);
    const sortedDates = rangeDates.length > 0
      ? rangeDates
      : Array.from(dateSet).sort((a, b) => a.localeCompare(b));
    const sortedEmployees = Array.from(matrix.keys()).sort((a, b) => a.localeCompare(b));

    const buildCell = (entries: OperationsLabourPreviewData[] | undefined): MatrixCell => {
      if (!entries || entries.length === 0) {
        return {
          hasData: false,
          entries: [],
          submissionCount: 0
        };
      }
      const mappedEntries = entries
        .slice()
        .sort((a, b) => String(a.submittedAt || '').localeCompare(String(b.submittedAt || '')))
        .map((item) => {
          const status = normalizeStatus(item.status || '');
          const statusType: 'approved' | 'declined' | 'unapproved' =
            status === 'approved' ? 'approved' : status === 'declined' ? 'declined' : 'unapproved';
          const statusLabel =
            status === 'approved'
              ? 'Approved'
              : status === 'declined'
                ? 'Declined'
                : status === 'need revisions' || status === 'revisions required' || status === 'revisions completed'
                  ? 'Revisions required'
                  : 'Unapproved';
          return {
            formId: String(item.id),
            workedHours: Number(item.submittedLabourHours || 0),
            hasOvertime: Boolean(item.hasOvertime || Number(item.overtimeHours || 0) > 0.0001),
            statusType,
            statusLabel,
            submittedAt: item.submittedAt || ''
          };
        });

      return {
        hasData: true,
        entries: mappedEntries,
        submissionCount: entries.length
      };
    };

    const rows = sortedEmployees.map((employeeName) => {
      const dateMap = matrix.get(employeeName) || new Map<string, OperationsLabourPreviewData[]>();
      const cells = new Map<string, MatrixCell>();
      for (const dateKey of sortedDates) {
        cells.set(dateKey, buildCell(dateMap.get(dateKey)));
      }
      return { employeeName, cells };
    });

    const totals = new Map<string, number>();
    for (const dateKey of sortedDates) {
      let total = 0;
      for (const row of rows) {
        const cell = row.cells.get(dateKey);
        if (cell?.entries?.length) {
          for (const entry of cell.entries) {
            total += Number(entry.workedHours || 0);
          }
        }
      }
      totals.set(dateKey, Math.round(total * 100) / 100);
    }

    return {
      dateColumns: sortedDates,
      employeeRows: rows,
      dateTotals: totals
    };
  }, [previewData, filters.startDate, filters.endDate]);

  const matrixTableMinWidth = Math.max(900, 220 + dateColumns.length * 120);
  const otBadgeStyle: React.CSSProperties = { backgroundColor: '#d9480f', color: '#fff' };

  React.useEffect(() => {
    let cancelled = false;
    const loadForemanAreas = async () => {
      try {
        const areas = await areasService.getForemanAreas();
        if (!cancelled) {
          setForemanAreas(Array.isArray(areas) ? areas : []);
        }
      } catch {
        if (!cancelled) {
          setForemanAreas([]);
        }
      }
    };
    loadForemanAreas();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredForemanAreas = useMemo(() => {
    if (!filters.serviceArea) {
      return foremanAreas;
    }
    const serviceAreaId = parseInt(filters.serviceArea, 10);
    if (Number.isNaN(serviceAreaId)) {
      return foremanAreas;
    }
    return foremanAreas.filter((area) => area.serviceAreaId === serviceAreaId);
  }, [foremanAreas, filters.serviceArea]);

  const getCellStyle = (cell: MatrixCell): React.CSSProperties => {
    if (!cell.hasData) return {};
    // Keep the cell neutral; entry-level rows are color coded individually.
    return { backgroundColor: '#ffffff' };
  };

  const getEntryStyle = (
    entry: MatrixCell['entries'][number]
  ): React.CSSProperties => {
    const { statusType } = entry;
    if (statusType === 'approved') {
      return {
        backgroundColor: '#b7ebc6',
        border: '1px solid #2f9e44',
        boxShadow: 'inset 0 0 0 1px rgba(47, 158, 68, 0.2)'
      };
    }
    if (statusType === 'declined') {
      return {
        backgroundColor: '#ffc9c9',
        border: '1px solid #c92a2a',
        boxShadow: 'inset 0 0 0 1px rgba(201, 42, 42, 0.2)'
      };
    }
    return {
      backgroundColor: '#ffe066',
      border: '1px solid #f08c00',
      boxShadow: 'inset 0 0 0 1px rgba(240, 140, 0, 0.2)'
    };
  };

  const getEntryLinkColor = (entry: MatrixCell['entries'][number]): string => {
    if (entry.statusType === 'approved') return '#1b5e20';
    if (entry.statusType === 'declined') return '#7f1d1d';
    return '#7a4e00';
  };

  const renderCellContent = (cell: MatrixCell): React.ReactNode => {
    if (!cell.hasData) return '';
    if (cell.entries.length === 0) return '';
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-py-1">
        {cell.submissionCount > 1 && (
          <Badge bg="danger">Multiple ({cell.submissionCount})</Badge>
        )}
        {cell.entries.map((entry) => {
          const hours = Math.round(entry.workedHours * 100) / 100;
          const formUrl = `/approvals?formId=${encodeURIComponent(entry.formId)}`;
          return (
            <div
              key={entry.formId}
              className="tw-flex tw-items-center tw-gap-1 tw-flex-wrap tw-justify-center tw-px-1 tw-py-1 tw-rounded"
              style={getEntryStyle(entry)}
            >
              <a
                href={formUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: getEntryLinkColor(entry),
                  textDecoration: 'underline',
                  fontWeight: 700
                }}
                title={`Status: ${entry.statusLabel} | Open form ${entry.formId} in new tab`}
              >
                {hours.toFixed(2)}
              </a>
              {entry.hasOvertime && (
                <Badge style={otBadgeStyle}>OT</Badge>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}

        <ReportParametersCard
          onGenerate={() => handleGeneratePreview()}
          onExport={handleExport}
          loadingGenerate={loadingPreview}
          loadingExport={loadingExport}
          disableExport={!pagination.total || loadingPreview}
          hideExportButton
          generateButtonText="Run Report"
          defaultCollapsed={false}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    serviceArea: e.target.value,
                    foremanArea: ''
                  })
                }
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Foreman Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.foremanArea}
                onChange={(e) => setFilters({ ...filters, foremanArea: e.target.value })}
              >
                <option value="">All Foreman Areas</option>
                {filteredForemanAreas.map((area) => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">From Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">To Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Overtime</Form.Label>
              <Form.Select
                size="sm"
                value={filters.overtimeFilter}
                onChange={(e) => setFilters({ ...filters, overtimeFilter: e.target.value })}
              >
                {OVERTIME_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>
      </div>

      <YellowHeaderInfo />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">Submitted time ({pagination.total} records)</h6>
            {pagination.total > 0 && (
              <Button variant="outline-primary" size="sm" onClick={handleExport} disabled={loadingExport}>
                <i className="bi bi-download tw-mr-1"></i>
                {loadingExport ? 'Exporting...' : 'Export CSV'}
              </Button>
            )}
          </Card.Header>
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'auto',
                width: '100%',
                borderTop: '1px solid #dee2e6',
                paddingBottom: '40px'
              }}
            >
              <Table
                striped
                bordered
                hover
                size="sm"
                style={{
                  marginBottom: 0,
                  minWidth: `${matrixTableMinWidth}px`,
                  width: `${matrixTableMinWidth}px`,
                  tableLayout: 'fixed'
                }}
              >
                <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fff' }}>
                  <tr>
                    <th
                      style={{
                        minWidth: '220px',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: '#fff',
                        zIndex: 11
                      }}
                      title="Employee Name"
                    >
                      Employee Name
                    </th>
                    {dateColumns.map((date) => (
                      <th key={date} style={{ minWidth: '110px', textAlign: 'center', whiteSpace: 'nowrap' }} title={date}>
                        {date}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!loadingPreview && employeeRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={dateColumns.length + 1}
                        className="tw-text-center tw-py-3"
                      >
                        No data available.
                      </td>
                    </tr>
                  )}
                  {employeeRows.map((row) => (
                    <tr key={row.employeeName}>
                      <td
                        style={{
                          position: 'sticky',
                          left: 0,
                          backgroundColor: '#fff',
                          zIndex: 9,
                          fontWeight: 600
                        }}
                      >
                        {row.employeeName}
                      </td>
                      {dateColumns.map((date) => {
                        const cell = row.cells.get(date)!;
                        return (
                          <td key={`${row.employeeName}-${date}`} style={{ textAlign: 'center', whiteSpace: 'nowrap', ...getCellStyle(cell) }}>
                            {renderCellContent(cell)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {employeeRows.length > 0 && (
                    <tr>
                      <td
                        style={{
                          position: 'sticky',
                          left: 0,
                          backgroundColor: '#f8f9fa',
                          zIndex: 9,
                          fontWeight: 700
                        }}
                      >
                        Total
                      </td>
                      {dateColumns.map((date) => (
                        <td key={`total-${date}`} style={{ textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 700, backgroundColor: '#f8f9fa' }}>
                          {(dateTotals.get(date) || 0).toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {loadingPreview && (
              <div className="tw-p-3 tw-text-center tw-text-content-muted tw-text-sm">
                Loading matrix...
              </div>
            )}
            {!loadingPreview && (
              <div className="tw-p-2 tw-border-t tw-border-line tw-text-sm">
                <div className="tw-flex tw-gap-2 tw-mb-2">
                  <Badge style={otBadgeStyle}>OT</Badge>
                  <Badge bg="warning" text="dark">Unapproved</Badge>
                  <Badge bg="success">Approved</Badge>
                  <Badge bg="danger">Declined</Badge>
                </div>
                <div className="tw-text-content-muted">
                  <strong>Matrix View:</strong> Employees are shown on the left and dates across columns. Cell colors: yellow = unapproved, green = approved, red = declined on WRAP. The OT badge is orange.
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default OperationsLabourReportView;
